import React, { useContext, useEffect, useState, useRef } from "react";

import { WeeklyContext } from "../../../context/global/WeeklyContext";

import ScopeSummaryBox from "./workScopeSummary/ScopeSummaryBox";
import NoScopesBox from "./workScopeSummary/NoScopesBox";

/**
 * Component that displays a summary of work scopes and assigned workers for a given job on a specific day.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.scopes - The list of work scopes for the day.
 * @param {Object} props.job - The job object containing job details.
 * @param {number} props.dayIndex - Visible column index in the current Dispatch strip.
 * @param {{ id: number, colorHex: string }|null} [props.hoveredBlock] - Hovered Schedule-block link shared across the job row.
 * @param {Function} [props.onBlockHover] - Setter for the hovered Schedule-block link.
 * @returns {JSX.Element} The rendered component.
 */
const WorkScopesSummary = ({
  scopes,
  job,
  dayIndex,
  hoveredBlock = null,
  onBlockHover = null,
}) => {
  // States and refs
  const { showWeekends, datesOfWeek } = useContext(WeeklyContext);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);

  // ResizeObserver to track container width for hover element positioning
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // How many scopes today match the Job ID and therefore should be in this section
  const numOfScopes = scopes
    ? scopes.filter((scope) => scope.job.id === job.id).length
    : 0;

  return (
    <div ref={containerRef}>
      {numOfScopes > 0 ? (
        <>
          {scopes.map((scope, index) => (
            <div key={scope.id} className="grid grid-cols-[100%_100%] gap-0">
              <div className="col-span-1">
                <ScopeSummaryBox
                  scope={scope}
                  job={job}
                  dayIndex={dayIndex}
                  visibleCount={datesOfWeek.length}
                  containerWidth={containerWidth}
                  showWeekends={showWeekends}
                  hoveredBlock={hoveredBlock}
                  onBlockHover={onBlockHover}
                />
              </div>
            </div>
          ))}
        </>
      ) : (
        <NoScopesBox />
      )}
    </div>
  );
};

export default WorkScopesSummary;
